import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, input, model } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';

@Component({
  selector: 'aura-number-input',
  standalone: true,
  imports: [CommonModule, FormsModule, MatFormFieldModule, MatInputModule, MatButtonModule, MatIconModule],
  template: `
    <mat-form-field appearance="outline" class="w-full" subscriptSizing="dynamic">
      @if (label()) {
        <mat-label>{{ label() }}</mat-label>
      }
      <input
        matInput
        type="number"
        [step]="step()"
        [min]="min()"
        [max]="max() || null"
        [(ngModel)]="value"
        [required]="required()"
        [placeholder]="placeholder()"
        [disabled]="disabled()"
      />
      <div matSuffix class="ml-2 flex h-full items-center gap-2">
        <button
          mat-icon-button
          (click)="decrement()"
          type="button"
          tabindex="-1"
          [disabled]="disabled() || (value() ?? 0) <= min()"
        >
          <mat-icon>remove</mat-icon>
        </button>
        <button
          mat-icon-button
          (click)="increment()"
          type="button"
          tabindex="-1"
          [disabled]="disabled() || (max() !== undefined && (value() ?? 0) >= max()!)"
        >
          <mat-icon>add</mat-icon>
        </button>
      </div>
      @if (required() && !value()) {
        <!-- We can add error handling here if we expose control or genericize error message -->
        <mat-error>Requis</mat-error>
      }
      @if (description()) {
        <mat-hint>{{ description() }}</mat-hint>
      }
    </mat-form-field>
  `,
  styles: [
    `
      /* Remove arrows/spinners from number input */
      input[type='number']::-webkit-inner-spin-button,
      input[type='number']::-webkit-outer-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }
      input[type='number'] {
        -moz-appearance: textfield; /* Firefox */
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AuraNumberInputComponent {
  label = input<string>('');
  value = model<number | undefined>(undefined); // Using model for two-way binding
  min = input<number>(0);
  max = input<number | undefined>(undefined);
  required = input<boolean>(false);
  disabled = input<boolean>(false);
  placeholder = input<string>('');
  description = input<string>('');
  step = input<number>(1);

  increment() {
    this.value.update((v) => {
      const current = v ?? 0;
      const step = this.step();
      const next = Number((current + step).toFixed(12));
      const maxVal = this.max();

      if (maxVal !== undefined && next > maxVal) {
        return maxVal;
      }
      return next;
    });
  }

  decrement() {
    this.value.update((v) => {
      const current = v ?? 0;
      const step = this.step();
      const next = Number((current - step).toFixed(12));

      if (next < this.min()) {
        return this.min();
      }
      return next;
    });
  }
}
